import { Injectable, OnDestroy } from '@angular/core';
import { initialize, LDClient, LDFlagSet, LDFlagValue, LDOptions } from 'launchdarkly-js-client-sdk';
import { catchError, filter, from, map, Observable, of, Subscription, tap } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import versionData from '../../../version.json';
import { Environment, EnvironmentService } from '../environment/environment.service';
import { ILoanApplication } from '../loan-application/loan-application.model';
import { LoanApplicationService } from '../loan-application/loan-application.service';
import { LoggingService } from '../logging/logging.service';
import { TagDataService } from '../tag-data/tag-data.service';

export enum FeatureFlagEnum {
	debitCardvelocityCheckEnabled = 'online-origination-debit-card-velocity-check-enabled',
	uAPromoCard = 'online-origination-ua-promo-card',
	coAppOfferScreen = 'co-app-offer-screen-feature-online',
	coAppInviteFlow = 'co-app-invite-flow-feature-online',
	shouldVerifyIncomeFunding = 'online-origination-verify-income-funding'
}

type FeatureFlagResponse = {
	[FeatureFlagEnum.debitCardvelocityCheckEnabled]: boolean;
	[FeatureFlagEnum.uAPromoCard]: boolean;
	[FeatureFlagEnum.coAppOfferScreen]: boolean;
	[FeatureFlagEnum.coAppInviteFlow]: boolean;
	[FeatureFlagEnum.shouldVerifyIncomeFunding]: boolean; // <-- Added for shouldVerifyIncome
};

type _FeatureFlagKeys = keyof FeatureFlagResponse;
export type FeatureFlagKeys = {
	[K in _FeatureFlagKeys]: K;
}[_FeatureFlagKeys];

/**
 * Service for managing feature flags using LaunchDarkly.
 * This service initializes the LaunchDarkly client, manages user contexts,
 * monitors feature flag changes, and provides utility methods for accessing
 * and observing feature flags.
 *
 * @export
 * @class FeatureFlagService
 * @implements {OnDestroy}
 */
@Injectable()
export class FeatureFlagService implements OnDestroy {
	private subscription = new Subscription();
	readonly keyMap = {
		[Environment.Prod]: '65b88b2438b5d10fac52c4e2',
		[Environment.Stage]: '65c2d2fd7480b80f8036aa62',
		[Environment.Qa]: '65c2d2ed07fe330fd42a5ce7',
		[Environment.Dev]: '65b88b2438b5d10fac52c4e1' // test env
	};
	readonly contextOnlineOriginationUserKind = 'ONLINE.ORIGINATION.USER';
	readonly contextOnlineOriginationUser = {
		kind: this.contextOnlineOriginationUserKind
	};

	readonly contextOnlineOriginationKind = 'ONLINE.ORIGINATION';
	readonly contextOnlineOrigination = {
		kind: this.contextOnlineOriginationKind,
		key: 'online-origination'
	};

	client: LDClient;
	flags: LDFlagSet;
	constructor(
		private environmentService: EnvironmentService,
		private loanAppService: LoanApplicationService,
		private tagDataService: TagDataService,
		private loggingService: LoggingService
	) {}

	async ngOnDestroy() {
		this.subscription.unsubscribe();
		await this.client.close();
	}

	init(): void {
		const env = this.environmentService.isInternal
			? environment.env
			: this.environmentService.isDev
			? Environment.Qa
			: this.environmentService.env;
		const envKey = this.keyMap[env];

		const options: LDOptions = {
			application: {
				id: 'online-origination',
				version: `${versionData.build_version}-${versionData.build_git_commit_id}`
			}
		};
		this.client = initialize(envKey, this.contextOnlineOrigination, options);

		this.client
			.waitForInitialization(5)
			.then(() => {
				this.initializeFlags();
			})
			.catch((err) => {
				this.loggingService.error(`Error waiting for client initialization:`, err);
				this.tagDataService.link({
					tealium_event: 'launch-darkly-init-failure',
					event_action: 'initialization',
					error_message: err?.message
				});
			});
		this.monitorLoanApp();
	}

	private monitorLoanApp(): void {
		const loanAppSub = this.loanAppService.loanApplication$
			.pipe(
				filter(Boolean),
				map((loanApp: ILoanApplication) => loanApp.id), // Extract the loanApp.id
				distinctUntilChanged(), // Only proceed if the id has changed
				switchMap((loanAppId) => from(this.client.waitUntilReady()).pipe(map(() => loanAppId))),
				tap((loanAppId) => this.setIdentity(loanAppId))
			)
			.subscribe();
		this.subscription.add(loanAppSub);
	}

	private initializeFlags() {
		this.flags = this.client.allFlags();
		// Log only the values that are part of the FeatureFlagEnum
		const filteredFlags = Object.values(FeatureFlagEnum)
			.filter((key) => key in this.flags)
			.reduce((acc, key) => ({ ...acc, [key]: this.flags[key] }), {} as Partial<LDFlagSet>);
		this.tagDataService.link({
			tealium_event: 'launch-darkly-flags',
			event_action: 'initialization',
			event_attr1: JSON.stringify(filteredFlags)
		});
	}

	private setIdentity(loanAppId: number): void {
		const multiContext = {
			kind: 'multi',
			[this.contextOnlineOriginationUserKind]: { ...this.contextOnlineOriginationUser, key: String(loanAppId) },
			[this.contextOnlineOriginationKind]: this.contextOnlineOrigination
		};

		this.client.identify(multiContext, null).then((flags: LDFlagSet) => {
			this.initializeFlags();
		});
	}

	/**
	 * Gets an observable that monitors the flag and any changes.
	 * NOTE: this creates a stream connection to launch darkly that is maintained.
	 * Any changes on launch darkly should get updated.
	 *
	 * @param {string} flagKey
	 * @param {LDFlagValue} [defaultValue=false]
	 * @return {*}  {Observable<LDFlagValue>}
	 * @memberof LaunchDarklyService
	 */
	public selectFlag(flagKey: FeatureFlagKeys, defaultValue: LDFlagValue = false): Observable<LDFlagValue> {
		return new Observable<LDFlagValue>((observer) => {
			const emitFlagValue = () => observer.next(this.getFlag(flagKey, defaultValue));
			this.client.on(`change:${flagKey}`, emitFlagValue);
			this.client
				.waitUntilReady()
				.then(emitFlagValue)
				.catch((err) => {
					this.loggingService.error(`Error waiting for client readiness for flag ${flagKey}:`, err);
					observer.error(err);
				});

			// Cleanup logic
			return () => this.client.off(`change:${flagKey}`, emitFlagValue);
		}).pipe(
			catchError((err) => {
				this.loggingService.error(`Error in selectFlag observable for ${flagKey}:`, err);
				return of(defaultValue);
			})
		);
	}

	/**
	 * Gets the value of a feature flag.
	 *
	 * @param {string} flagKey
	 * @param {LDFlagValue} [defaultValue=false]
	 * @return {*}  {LDFlagValue}
	 * @memberof LaunchDarklyService
	 */
	public getFlag(flagKey: FeatureFlagKeys, defaultValue: LDFlagValue = false): LDFlagValue {
		return this.client.variation(flagKey, defaultValue);
	}
}
